// WbNewTicketPage — /weighbridge/tickets/new (Phase C10 Group 1).
//
// Owns the 3-pane shell and the Start modal that stages
// direction/weighing_mode/yard/weighbridge on WbTicketContext.pendingShell
// BEFORE any POST /wb/tickets. The actual POST fires from Group 2's left
// rail once a customer is picked (the server requires customer_enc_id).
//
// Routes:
//   /weighbridge/tickets/new            → fresh start, modal opens
//   /weighbridge/tickets/new?from=ENC   → hydrate from an existing draft
//                                         and skip the modal
//
// Pane placeholders carry data-testid="wb-{left,centre,right}-rail-slot" so
// the Integration agent can swap them for Groups 2/3/4 components.

import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { useWbTicket } from '../context/WbTicketContext';
import { useWbTicket as useWbTicketQuery } from '../hooks/useWbTicket';
import { WbTicketHeader } from './components/WbTicketHeader';
import { WbTicketStartModal } from './components/WbTicketStartModal';
import { WbTicketLeftRail } from './components/WbTicketLeftRail';
import { WbTicketCentrePane } from './components/WbTicketCentrePane';
import { WbTicketRightRail } from './components/WbTicketRightRail';
import type { PendingTicketShell } from '../context/WbTicketContext';
import styles from './WbNewTicketPage.module.css';

export function WbNewTicketPage(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromEncId = searchParams.get('from');

  const {
    ticket,
    pendingShell,
    setPendingShell,
    hydrateFromTicket,
    resetTicket,
  } = useWbTicket();

  // -- Hydration from ?from=enc_id ---------------------------------------
  //
  // When `from` is present we load the existing draft via the standalone
  // useWbTicket hook (the query one — renamed at import time to dodge a
  // clash with the context hook) and push the result into the context.
  // The modal stays closed in this flow.
  const ticketQuery = useWbTicketQuery(fromEncId ?? undefined);

  // Hydrate exactly ONCE per `?from=` value. We must NOT compare the context
  // ticket's enc_id against the query's enc_id — the server re-encrypts the
  // numeric id on every response, so those two enc_ids never match after the
  // first PATCH echo, which made the old guard re-hydrate (and wipe the
  // operator's edits) on every autosave. The URL param is the only stable
  // identity for the page session; key the one-shot off it. After hydration,
  // PATCH echoes flow through the context's applyTicketIfFresh, not here.
  const hydratedFromRef = useRef<string | null>(null);
  useEffect(() => {
    if (fromEncId === null) return;
    const data = ticketQuery.data;
    if (!data?.ticket) return;
    if (hydratedFromRef.current === fromEncId) return;
    hydratedFromRef.current = fromEncId;
    hydrateFromTicket(data.ticket);
  }, [fromEncId, ticketQuery.data, hydrateFromTicket]);

  // -- Ticket lock (migration 167) --------------------------------------
  //
  // The continue-ticket editor is where editing actually happens, so it owns
  // the advisory lock: acquire on mount, 30s heartbeat, release on unmount.
  // The detail page (WbTicketDetailPage) only READS the lock for its banner.
  //
  // Lock acquire is fire-and-forget / non-fatal — the editor opens regardless
  // of whether the lock was granted. `locked_by_name` and `session_jti` are
  // derived server-side from the staff token (the body only carries `origin`),
  // so there's no staff identity to pass from the client.
  //
  // A fresh ticket (no `?from=`) has no server row to lock yet, so the lock
  // effects no-op until `fromEncId` is present.
  useEffect(() => {
    if (fromEncId === null) return;
    void Promise.resolve(
      apiClient.post(endpoints.wb.ticketLock(fromEncId), { origin: 'main' }),
    ).catch(() => {
      // Non-fatal: the editor still opens; the detail page surfaces the holder.
    });
  }, [fromEncId]);

  useEffect(() => {
    if (fromEncId === null) return undefined;
    const interval = setInterval(() => {
      void Promise.resolve(
        apiClient.post(endpoints.wb.ticketLockHeartbeat(fromEncId), {}),
      ).catch(() => {});
    }, 30_000);
    return () => clearInterval(interval);
  }, [fromEncId]);

  // -- Cleanup on unmount -----------------------------------------------
  //
  // The context outlives the page (it lives at WeighbridgeLayout). Without
  // a reset, navigating away and back would leak the previous ticket /
  // pendingShell into the next visit. resetTicket() also tears down any
  // in-flight autosave / price-resolve timers.
  //
  // We also release the ticket lock here: navigating away (incl. after a
  // complete/void, which routes back to the list) unmounts the page and frees
  // the lock for the next editor. `fromEncId` is captured at mount and stable
  // for the page session, so closing over it targets the right ticket.
  useEffect(() => {
    return () => {
      resetTicket();
      if (fromEncId !== null) {
        void Promise.resolve(
          apiClient.delete(endpoints.wb.ticketLock(fromEncId)),
        ).catch(() => {});
      }
    };
    // `fromEncId` is intentionally NOT in the deps: it is stable for the page
    // session (a query param on the static /tickets/new route), captured at
    // mount. Adding it would re-register the cleanup on every param change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetTicket]);

  // -- Start modal handlers ---------------------------------------------
  function handleStart(shell: PendingTicketShell) {
    setPendingShell(shell);
  }

  function handleCancel() {
    navigate('/weighbridge/tickets');
  }

  // Modal is OPEN only when there is no ticket AND no staged shell AND no
  // ?from= flow in progress (loading or hydrated). For the ?from= flow we
  // never want the modal regardless of state.
  const showModal =
    fromEncId === null && ticket === null && pendingShell === null;

  // Render the 3-pane shell once we have EITHER a hydrated ticket OR a
  // staged pendingShell. Until then we render the modal on its own.
  const hasShell = ticket !== null || pendingShell !== null;

  return (
    <div className={styles.page}>
      {hasShell ? (
        <>
          <WbTicketHeader />
          <div className={styles.threePane}>
            <aside
              className={styles.leftRail}
              data-testid="wb-left-rail-slot"
            >
              <WbTicketLeftRail />
            </aside>
            <main
              className={styles.centrePane}
              data-testid="wb-centre-pane-slot"
            >
              <WbTicketCentrePane />
            </main>
            <aside
              className={styles.rightRail}
              data-testid="wb-right-rail-slot"
            >
              <WbTicketRightRail />
            </aside>
          </div>
        </>
      ) : null}

      <WbTicketStartModal
        open={showModal}
        onCancel={handleCancel}
        onStart={handleStart}
      />
    </div>
  );
}
